import { Link } from 'react-router-dom';
import { ArrowUpRight, Award, ShoppingBag } from 'lucide-react';
import { HERITAGE_ESTATE, HERITAGE_BUNDLES } from '@/data/catalog';

/**
 * FlagshipBand
 *
 * A three-card horizontal editorial band highlighting the Heritage
 * Estate flagship SKUs (Waxed Jute + Hemp Tinder) plus the top bundle
 * (Highland Fire Kit). Sits between the E-Bikes range and the
 * UserFriendlyFeatures block on the homepage. Reads as a "new from the
 * estate" note; the bundle tile pushes AOV straight from the home hero.
 */
export default function FlagshipBand() {
  const flagships = HERITAGE_ESTATE.filter((p) => p.featured);
  const bundle = HERITAGE_BUNDLES[0]; // Highland Fire Kit — the anchor bundle
  if (flagships.length === 0) return null;

  return (
    <section
      data-testid="flagship-band"
      className="relative bg-[var(--brand-forest-deep)] text-[var(--brand-parchment)] overflow-hidden"
    >
      <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="flex items-end justify-between gap-6 mb-10 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Award size={16} className="text-[var(--brand-gold)]" strokeWidth={1.8} />
              <span className="font-eyebrow !text-[var(--brand-gold)]">
                New · Heritage Estate
              </span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight max-w-2xl">
              Two flagships and a kit.{' '}
              <span className="italic text-[var(--brand-gold)]">
                Built for the hill, not the Instagram.
              </span>
            </h2>
          </div>
          <Link
            to="/heritage-estate"
            data-testid="flagship-band-see-all"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--brand-gold)] link-expand whitespace-nowrap"
          >
            See all 11 in the collection <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {flagships.map((p) => (
            <Link
              to={`/product/${p.slug}`}
              key={p.slug}
              data-testid={`flagship-band-card-${p.slug}`}
              className="group relative flex flex-col bg-[var(--brand-parchment)] text-[var(--brand-ink)] overflow-hidden hover:ring-1 hover:ring-[var(--brand-gold)] transition-all"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-[var(--brand-parchment-warm)]">
                <img
                  src={p.image}
                  alt={p.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute top-3 left-3 bg-[var(--brand-forest)] text-[var(--brand-parchment)] text-[10px] tracking-[0.22em] uppercase px-2.5 py-1">
                  {p.badge}
                </span>
              </div>
              <div className="p-5 flex flex-col grow">
                <div className="text-[10px] tracking-[0.24em] uppercase text-[var(--brand-muted)] font-semibold">
                  {p.sku} · {p.category}
                </div>
                <h3 className="mt-2 font-display text-xl text-[var(--brand-forest)] leading-tight tracking-tight">
                  {p.name}
                </h3>
                <p className="mt-2 text-sm text-[var(--brand-ink)] leading-relaxed line-clamp-3 grow">
                  {p.summary || p.blurb}
                </p>
                <div className="mt-4 pt-4 flex items-center justify-between border-t border-[var(--brand-stone)]">
                  <span className="font-display text-lg font-semibold text-[var(--brand-olive)]">
                    {p.price}
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--brand-forest)] group-hover:text-[var(--brand-gold)] transition-colors">
                    View <ArrowUpRight size={12} />
                  </span>
                </div>
              </div>
            </Link>
          ))}

          {/* Bundle teaser tile — pushes AOV from the home hero */}
          {bundle && (
            <Link
              to={`/product/${bundle.slug}`}
              data-testid={`flagship-band-bundle-${bundle.slug}`}
              className="group relative flex flex-col bg-[var(--brand-parchment)] text-[var(--brand-ink)] overflow-hidden hover:ring-1 hover:ring-[var(--brand-gold)] transition-all"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-[var(--brand-parchment-warm)]">
                <img
                  src={bundle.image}
                  alt={bundle.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute top-3 left-3 bg-[var(--brand-gold)] text-[var(--brand-forest-deep)] text-[10px] tracking-[0.22em] uppercase px-2.5 py-1 font-bold">
                  {bundle.saving}
                </span>
              </div>
              <div className="p-5 flex flex-col grow">
                <div className="text-[10px] tracking-[0.24em] uppercase text-[var(--brand-muted)] font-semibold flex items-center gap-1.5">
                  <ShoppingBag size={11} /> {bundle.sku} · Curated Bundle
                </div>
                <h3 className="mt-2 font-display text-xl text-[var(--brand-forest)] leading-tight tracking-tight">
                  {bundle.name}
                </h3>
                <p className="mt-2 text-sm text-[var(--brand-ink)] leading-relaxed line-clamp-3 grow">
                  {bundle.blurb}
                </p>
                <div className="mt-4 pt-4 flex items-center justify-between border-t border-[var(--brand-stone)]">
                  <span className="flex items-baseline gap-2">
                    <span className="font-display text-lg font-semibold text-[var(--brand-olive)]">
                      {bundle.price}
                    </span>
                    <span className="text-xs text-[var(--brand-muted)] line-through">
                      {bundle.rrp}
                    </span>
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--brand-forest)] group-hover:text-[var(--brand-gold)] transition-colors">
                    View <ArrowUpRight size={12} />
                  </span>
                </div>
              </div>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}
